import Head from 'next/head';
import { useRouter } from 'next/router';
import i18nextConfig from '../../next-i18next.config';

const BASE_URL = 'https://www.penetrify.cloud';

// Serialize JSON-LD safely: escape <, > and & so values cannot break out of the <script> tag.
function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

interface SEOProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  article?: {
    publishedTime: string;
    author?: string;
  };
  noindex?: boolean;
}

export default function SEO({ title, description, ogImage, ogType = 'website', article, noindex }: SEOProps) {
  const router = useRouter();
  const locale = (router.query.locale as string) || 'en';
  const pathWithoutLocale = router.asPath.replace(`/${locale}`, '');
  const canonical = `${BASE_URL}/${locale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`;
  const image = ogImage ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`) : `${BASE_URL}/og-image.jpg`;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <link rel="canonical" href={canonical} />
      {i18nextConfig.i18n.locales.map((loc: string) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${BASE_URL}/${loc}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content="Penetrify" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.author && <meta property="article:author" content={article.author} />}
        </>
      )}
    </Head>
  );
}

export function OrganizationJsonLd() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Penetrify',
    url: BASE_URL,
    logo: `${BASE_URL}/favicon.svg`,
    description: 'Autonomous AI-powered penetration testing platform',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Nové sady 988/2',
      addressLocality: 'Brno',
      postalCode: '602 00',
      addressCountry: 'CZ',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@penetrify.cloud',
      contactType: 'sales',
    },
  };

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }} />
    </Head>
  );
}

export function SoftwareApplicationJsonLd() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Penetrify',
    applicationCategory: 'SecurityApplication',
    operatingSystem: 'Web',
    offers: [
      {
        '@type': 'Offer',
        name: 'Starter',
        price: '50',
        priceCurrency: 'USD',
        priceValidUntil: '2027-12-31',
      },
      {
        '@type': 'Offer',
        name: 'Professional',
        price: '600',
        priceCurrency: 'USD',
        priceValidUntil: '2027-12-31',
      },
      {
        '@type': 'Offer',
        name: 'Enterprise',
        price: '2500',
        priceCurrency: 'USD',
        priceValidUntil: '2027-12-31',
      },
    ],
    description: 'Autonomous AI Red Team. Deploy elite-level penetration testing into your CI/CD pipeline in minutes.',
  };

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }} />
    </Head>
  );
}

export function ArticleJsonLd({ title, description, image, datePublished, url }: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  url: string;
}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image.startsWith('http') ? image : `${BASE_URL}${image}`,
    datePublished,
    author: {
      '@type': 'Organization',
      name: 'Penetrify',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Penetrify',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/favicon.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }} />
    </Head>
  );
}
